import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  model,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { LongPressDirective } from '@directives/long-press.directive';
import {
  hoursValidatorPattern,
  minutesValidatorPattern,
  pad,
  secondsValidatorPattern,
} from 'app/utils/datetime';

@Component({
  selector: 'abb-time-input',
  template: `
    <table class="table text-abb-font">
      <tbody class="tbody">
        <tr>
          <td>
            <button
              id="increment-hours"
              type="button"
              mat-icon-button
              (click)="increment(hours(), inputHours)"
              longPress
              (mouseLongPress)="increment(hours(), inputHours)"
            >
              <abb-icon
                aria-label="expand_less icon"
                [name]="'mat/expand_less'"
              ></abb-icon>
            </button>
          </td>
          <td></td>
          <td>
            <button
              id="increment-minutes"
              type="button"
              mat-icon-button
              (click)="increment(minutes(), inputMinutes)"
              longPress
              (mouseLongPress)="increment(minutes(), inputMinutes)"
            >
              <abb-icon
                aria-label="expand_less icon"
                [name]="'mat/expand_less'"
              ></abb-icon>
            </button>
          </td>
          <td></td>
          <td>
            <button
              id="increment-seconds"
              type="button"
              mat-icon-button
              (click)="increment(seconds(), inputSeconds)"
              longPress
              (mouseLongPress)="increment(seconds(), inputSeconds)"
            >
              <abb-icon
                aria-label="expand_less icon"
                [name]="'mat/expand_less'"
              ></abb-icon>
            </button>
          </td>
          <td></td>
        </tr>
        <tr>
          <td>
            <div class="input-wrapper">
              <div class="input-infix">
                <input
                  class="bg-transparent"
                  [ngModel]="hours()"
                  id="hours"
                  name="hours"
                  #inputHours
                  (ngModelChange)="updateValue($event, inputHours)"
                />
              </div>
            </div>
          </td>
          <td class="spacer">:</td>
          <td>
            <div class="input-wrapper">
              <div class="input-infix">
                <input
                  class="bg-transparent"
                  [ngModel]="minutes()"
                  id="minutes"
                  name="minutes"
                  #inputMinutes
                  (ngModelChange)="updateValue($event, inputMinutes)"
                />
              </div>
            </div>
          </td>
          <td class="spacer">:</td>
          <td>
            <div class="input-wrapper">
              <div class="input-infix">
                <input
                  class="bg-transparent"
                  [ngModel]="seconds()"
                  id="seconds"
                  name="seconds"
                  #inputSeconds
                  (ngModelChange)="updateValue($event, inputSeconds)"
                />
              </div>
            </div>
          </td>
        </tr>
        <tr>
          <td>
            <button
              type="button"
              id="decrement-hour"
              mat-icon-button
              (click)="decrement(hours(), inputHours)"
              longPress
              (mouseLongPress)="decrement(hours(), inputHours)"
            >
              <abb-icon
                aria-label="expand_more icon"
                [name]="'mat/expand_more'"
              ></abb-icon>
            </button>
          </td>
          <td></td>
          <td>
            <button
              type="button"
              id="decrement-minutes"
              mat-icon-button
              (click)="decrement(minutes(), inputMinutes)"
              longPress
              (mouseLongPress)="decrement(minutes(), inputMinutes)"
            >
              <abb-icon
                aria-label="expand_more icon"
                [name]="'mat/expand_more'"
              ></abb-icon>
            </button>
          </td>
          <td></td>
          <td>
            <button
              type="button"
              id="decrement-seconds"
              mat-icon-button
              (click)="decrement(seconds(), inputSeconds)"
              longPress
              (mouseLongPress)="decrement(seconds(), inputSeconds)"
            >
              <abb-icon
                aria-label="expand_more icon"
                [name]="'mat/expand_more'"
              ></abb-icon>
            </button>
          </td>
          <td></td>
        </tr>
      </tbody>
    </table>
  `,
  styles: `
    .table {
      display: table;
      border-collapse: separate;
      box-sizing: border-box;
      text-indent: initial;
      border-spacing: 2px;
      border-color: gray;
    }
    .tbody {
      display: table-row-group;
      vertical-align: middle;
      border-color: inherit;
    }
    .tr {
      display: table-row;
      vertical-align: inherit;
      border-color: inherit;
    }
    .td {
      display: table-cell;
      vertical-align: inherit;
    }
    .spacer {
      font-weight: 700;
    }
    .input-wrapper {
      display: inline-flex;
      justify-content: center;
      align-items: baseline;
      box-sizing: border-box;
      width: 100%;
      .input-infix {
        padding: .4375em 0;
        display: block;
        position: relative;
        flex: auto;
        width: 20px;
        max-width: 20px;
        text-align: center;
        input {
          margin-top: -.0625em;
          outline: none;
          width: 100%;
          max-width: 100%;
          text-align: center;
        }
      }
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AbbTimeInputComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    AbbIconComponent,
    LongPressDirective,
  ],
})
export class AbbTimeInputComponent implements ControlValueAccessor, OnDestroy {
  readonly hoursValidatorPattern = hoursValidatorPattern;
  readonly minutesValidatorPattern = minutesValidatorPattern;
  readonly secondsValidatorPattern = secondsValidatorPattern;

  @ViewChild('inputHours') inputHours!: HTMLInputElement;
  @ViewChild('inputMinutes') inputMinutes!: HTMLInputElement;
  @ViewChild('inputSeconds') inputSeconds!: HTMLInputElement;

  private timeoutId: NodeJS.Timeout | undefined = undefined;

  hours = model<string>(pad('0', 2, ''));
  minutes = model<string>(pad('0', 2, ''));
  seconds = model<string>(pad('0', 2, ''));

  value: string | null = null;
  disabled = false;
  required = signal(false);
  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: string): void {
    if (value) {
      this.value = value;
      const [hours, minutes, seconds] = value.split(':');
      this.hours.set(hours || '00');
      this.minutes.set(minutes || '00');
      this.seconds.set(seconds || '00');
      
      this.onChange(this.value);
    } else {
      this.value = '00:00:00';
      this.onChange(this.value);
      
      this.timeoutId = setTimeout(() => {
        if (this.inputHours && this.inputMinutes && this.inputSeconds) {
          this.updateValue('00', this.inputHours);
          this.updateValue('00', this.inputMinutes);
          this.updateValue('00', this.inputSeconds);
        }
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  updateValue(v: string, input: HTMLInputElement) {
    const padVal = pad(parseInt(v), 2, '');
    switch (input.name) {
      case 'hours': {
        const hRegexp = new RegExp(this.hoursValidatorPattern());
        if (!hRegexp.test(padVal)) {
          input.value = this.hours();
          break;
        }
        this.hours.set(padVal);
        break;
      }
      case 'minutes': {
        const mRegexp = new RegExp(this.minutesValidatorPattern());
        if (!mRegexp.test(padVal)) {
          input.value = this.minutes();
          break;
        }
        this.minutes.set(padVal);
        break;
      }
      case 'seconds': {
        const sRegexp = new RegExp(this.secondsValidatorPattern());
        if (!sRegexp.test(padVal)) {
          input.value = this.seconds();
          break;
        }
        this.seconds.set(padVal);
        break;
      }
    }
    const value = `${this.hours()}:${this.minutes()}:${this.seconds()}`;
    this.onChange(value || '00:00:00');
  }

  increment(v: string, input: HTMLInputElement) {
    this.updateValue((parseInt(v) + 1).toString(), input);
  }

  decrement(v: string, input: HTMLInputElement) {
    this.updateValue((parseInt(v) - 1).toString(), input);
  }

  ngOnDestroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}
